// Globals
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import {
  FormColumn,
  StyledActionButton,
  StyledButton,
  StyledButtonsWrap,
  StyledForm,
  StyledLink,
  StyledSelect,
  StyledWrapper,
} from './FeedFormStyles';
import { FormItem, IFormInstance } from '@/components';
import { CheckmarkIcon } from '@/icons';
import { Checkbox, Input, InputNumber, Radio, RadioGroup, Select, Tooltip } from '@/ui';
import { Form, Modal, Tag } from 'antd';

// Modules
import { LOCALES_FIELD_NAME, LocalesFormFields } from '@/modules/Locales';
import { useAutocompleteRegions } from '@/modules/Regions/hooks';
import { getCategories } from '@/modules/Categories';
import { CategoryMultiTreeSelect } from '@/modules/SharedCategories/components';
import { useCategoriesSelect } from '@/modules/SharedCategories/hooks';
import { useStoresAutocomplete } from '@/modules/Stores/hooks';
import { Resource, usePermissions } from '@/modules/Permissions';
import { SupportedLocales } from '@/models/localization.model';
import { useAppDispatch } from '@/store';

// Models
import { FeedGenerationStatuses, IFeed } from '@/models/feeds.model';

// Helpers
import { FeedsFormFieldsNames, TFeedFormValues } from '../../helpers/types';
import { normalizeFeedFormValues } from '../../helpers';
import { useFeedsFormRules } from '../../hooks';

// API
import { FeedsAPI } from '@/api';

interface IFeedForm {
  onSubmit: (values: TFeedFormValues) => void;
  handleRegenerateFeed: () => void;
  isLoading: boolean;
  initialValues?: IFeed | null;
  formInstance: IFormInstance;
  handleExport: (locale: SupportedLocales) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  [FeedGenerationStatuses.PENDING]: { color: 'orange', label: 'feeds_list_generation_status_pending' },
  [FeedGenerationStatuses.PROCESSING]: { color: 'blue', label: 'feeds_list_generation_status_processing' },
  [FeedGenerationStatuses.FINISHED]: { color: 'green', label: 'feeds_list_generation_status_finished' },
  [FeedGenerationStatuses.FAILED]: { color: 'red', label: 'feeds_list_generation_status_failed' },
};

const FEED_TYPE_OPTIONS = [
  { label: 'Google Merchant', value: 'google' },
  { label: 'Meta (Facebook+Instagram)', value: 'meta' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'Google Local Inventory (залишки по РЦ)', value: 'google_local' },
];

const FeedForm = ({
  onSubmit,
  handleRegenerateFeed,
  isLoading,
  initialValues,
  formInstance,
  handleExport,
  onRefresh,
}: IFeedForm) => {
  const { t } = useTranslation(['seo']);
  const { canManage } = usePermissions();
  const canManageFeeds = canManage(Resource.FEED);
  const dispatch = useAppDispatch();
  const isEdit = !!initialValues?.id;
  const rules = useFeedsFormRules(isEdit);
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions('', 100);
  const {
    categories,
    autocompleteCategories,
    onSearch: onCategoriesSearch,
    expandedItems,
    handleExpandTreeItemData,
  } = useCategoriesSelect(undefined);
  const { options: storesOptions, onSearch: onStoresSearch } = useStoresAutocomplete();
  const watchedStoreIds = Form.useWatch(FeedsFormFieldsNames.STORE_IDS, formInstance);
  const watchedAllCategories = Form.useWatch(FeedsFormFieldsNames.ALL_CATEGORIES, formInstance);

  const [productCount, setProductCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [previewXml, setPreviewXml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    dispatch(getCategories());
    onSearchRegions('');
  }, [dispatch]);

  // Polling while feed is pending/processing
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const generationStatus = initialValues?.generation_status;
  const isGenerating =
    generationStatus === FeedGenerationStatuses.PENDING || generationStatus === FeedGenerationStatuses.PROCESSING;

  useEffect(() => {
    if (isGenerating && onRefresh) {
      pollingRef.current = setInterval(() => {
        onRefresh();
      }, 5000);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isGenerating, onRefresh]);

  const initialFormValues = useMemo(() => normalizeFeedFormValues(initialValues), [initialValues]);
  const isFeedActive = initialValues?.generation_status === FeedGenerationStatuses.FINISHED && initialValues?.active;

  const placeholder = t('common_placeholder_input', { ns: 'common' });

  const extractIds = (items: unknown): string[] => {
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => (typeof item === 'string' ? item : item?.value ?? item?.id ?? '')).filter(Boolean);
  };

  const getFilterPayload = useCallback(() => {
    const values = formInstance.getFieldsValue();
    const allCategories = !!values[FeedsFormFieldsNames.ALL_CATEGORIES];
    return {
      type: values[FeedsFormFieldsNames.TYPE] || 'google',
      region_ids: extractIds(values[FeedsFormFieldsNames.REGION_ID]),
      category_ids: allCategories ? [] : extractIds(values[FeedsFormFieldsNames.CATEGORIES]),
      store_ids: extractIds(values[FeedsFormFieldsNames.STORE_IDS]),
      min_price: values[FeedsFormFieldsNames.MIN_PRICE] ?? null,
      max_price: values[FeedsFormFieldsNames.MAX_PRICE] ?? null,
      in_stock_only: values[FeedsFormFieldsNames.IN_STOCK_ONLY] || false,
      all_categories: allCategories,
    };
  }, [formInstance]);

  const handleCountProducts = async () => {
    const payload = getFilterPayload();
    if (!payload.region_ids.length || (!payload.all_categories && !payload.category_ids.length)) return;

    setCountLoading(true);
    try {
      const response = await FeedsAPI.countProducts(payload);
      setProductCount(response.data.count);
    } catch {
      setProductCount(null);
    } finally {
      setCountLoading(false);
    }
  };

  const handlePreviewXml = async () => {
    const payload = getFilterPayload();
    if (!payload.region_ids.length || (!payload.all_categories && !payload.category_ids.length)) return;

    setPreviewLoading(true);
    try {
      const response = await FeedsAPI.previewXml(payload);
      setPreviewXml(response.data);
      setPreviewOpen(true);
    } catch {
      setPreviewXml(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <StyledWrapper>
      <StyledForm onFinish={onSubmit} clearOnDestroy initialValues={initialFormValues} form={formInstance}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          {/* Left column - Basic settings */}
          <FormColumn>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{t('feeds_section_basic')}</div>

            <FormItem name={LOCALES_FIELD_NAME}>
              <LocalesFormFields labelPrefix={'feeds_form_'} ns={'seo'} rules={rules[FeedsFormFieldsNames.NAME]} />
            </FormItem>

            <FormItem
              name={FeedsFormFieldsNames.SLUG}
              label={t('feeds_alias')}
              rules={rules[FeedsFormFieldsNames.SLUG]}
            >
              <Input placeholder={placeholder} size="large" />
            </FormItem>

            <FormItem name={FeedsFormFieldsNames.TYPE} label={t('feeds_type_label')}>
              <Select options={FEED_TYPE_OPTIONS} size="large" />
            </FormItem>

            <FormItem
              name={FeedsFormFieldsNames.ACTIVE}
              label={t('feeds_list_status')}
              rules={rules[FeedsFormFieldsNames.ACTIVE]}
            >
              <RadioGroup>
                <Radio value={true}>{t('feeds_active')}</Radio>
                <Radio value={false}>{t('feeds_disabled')}</Radio>
              </RadioGroup>
            </FormItem>

            {isEdit && generationStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 500 }}>{t('feeds_list_generation_status')}:</span>
                <Tag color={STATUS_CONFIG[generationStatus]?.color}>{t(STATUS_CONFIG[generationStatus]?.label)}</Tag>
                {isGenerating && <span style={{ fontSize: 12, color: '#999' }}>({t('feeds_polling')})</span>}
              </div>
            )}
          </FormColumn>

          {/* Right column - Filtering */}
          <FormColumn>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{t('feeds_section_filters')}</div>

            <FormItem name={FeedsFormFieldsNames.ALL_CATEGORIES} valuePropName="checked" hidden>
              <Checkbox />
            </FormItem>

            <FormItem
              name={FeedsFormFieldsNames.CATEGORIES}
              label={
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span>{t('feeds_list_categories')}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !watchedAllCategories;
                      formInstance.setFieldValue(FeedsFormFieldsNames.ALL_CATEGORIES, next);
                      if (next) {
                        formInstance.setFieldValue(FeedsFormFieldsNames.CATEGORIES, []);
                      }
                    }}
                    style={{
                      fontSize: 12,
                      background: 'none',
                      border: 'none',
                      color: '#1890ff',
                      cursor: 'pointer',
                      padding: 0,
                      fontWeight: watchedAllCategories ? 600 : 400,
                    }}
                  >
                    {watchedAllCategories ? t('feeds_all_categories_cancel') : t('feeds_all_categories_label')}
                  </button>
                </div>
              }
              rules={watchedAllCategories ? [] : rules[FeedsFormFieldsNames.CATEGORIES]}
            >
              <CategoryMultiTreeSelect
                categories={categories}
                autocompleteCategories={autocompleteCategories}
                onSearch={onCategoriesSearch}
                expandedItems={expandedItems}
                handleExpandTreeItemData={handleExpandTreeItemData}
                disabled={watchedAllCategories}
                placeholder=""
                checkable
              />
            </FormItem>

            <FormItem
              name={FeedsFormFieldsNames.REGION_ID}
              label={
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span>{t('feeds_list_region')}</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (regionsOptions?.length) {
                        formInstance.setFieldValue(
                          FeedsFormFieldsNames.REGION_ID,
                          regionsOptions.map((o: any) => o.value),
                        );
                      }
                    }}
                    style={{
                      fontSize: 12,
                      background: 'none',
                      border: 'none',
                      color: '#1890ff',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {t('feeds_select_all')}
                  </button>
                </div>
              }
              rules={rules[FeedsFormFieldsNames.REGION_ID]}
            >
              <StyledSelect
                allowClear
                showSearch
                filterOption={false}
                options={regionsOptions ?? []}
                onSearch={onSearchRegions}
                isMultiselect
                maxTagCount={100}
              />
            </FormItem>

            <Tooltip title={t('feeds_in_stock_only_tooltip')}>
              <FormItem name={FeedsFormFieldsNames.IN_STOCK_ONLY} valuePropName="checked">
                <Checkbox>{t('feeds_in_stock_only_label')}</Checkbox>
              </FormItem>
            </Tooltip>

            <FormItem name={FeedsFormFieldsNames.STORE_IDS} label={t('feeds_stores_label')}>
              <StyledSelect
                allowClear
                showSearch
                filterOption={false}
                options={storesOptions ?? []}
                onSearch={onStoresSearch}
                isMultiselect
                maxTagCount={100}
                placeholder={t('feeds_stores_placeholder')}
              />
            </FormItem>

            {watchedStoreIds?.length > 0 && (
              <Tooltip title={t('feeds_exclude_from_general_tooltip')}>
                <FormItem name={FeedsFormFieldsNames.EXCLUDE_FROM_GENERAL} valuePropName="checked">
                  <Checkbox>{t('feeds_exclude_from_general_label')}</Checkbox>
                </FormItem>
              </Tooltip>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormItem name={FeedsFormFieldsNames.MIN_PRICE} label={t('feeds_min_price_label')}>
                <InputNumber min={0} size="large" style={{ width: '100%' }} placeholder="0" />
              </FormItem>

              <FormItem name={FeedsFormFieldsNames.MAX_PRICE} label={t('feeds_max_price_label')}>
                <InputNumber min={0} size="large" style={{ width: '100%' }} placeholder="∞" />
              </FormItem>
            </div>

            {/* Count & Preview */}
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <StyledActionButton onClick={handleCountProducts} loading={countLoading} style={{ flex: 1 }}>
                {t('feeds_count_products')}
                {productCount !== null && ` (${productCount.toLocaleString()})`}
              </StyledActionButton>
              <StyledActionButton onClick={handlePreviewXml} loading={previewLoading} style={{ flex: 1 }}>
                {t('feeds_preview_xml')}
              </StyledActionButton>
            </div>
          </FormColumn>
        </div>

        <StyledButtonsWrap>
          {isEdit && isFeedActive && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
              <StyledLink href={initialValues?.files?.uk} target="_blank">
                {t('feeds_open_ua')}
              </StyledLink>
              <StyledLink href={initialValues?.files?.ru} target="_blank">
                {t('feeds_open_ru')}
              </StyledLink>
              <StyledActionButton onClick={() => handleExport(SupportedLocales.UK)}>
                {t('feeds_download_ua')}
              </StyledActionButton>
              <StyledActionButton onClick={() => handleExport(SupportedLocales.RU)}>
                {t('feeds_download_ru')}
              </StyledActionButton>
            </div>
          )}
          {canManageFeeds && isEdit && (
            <StyledButton onClick={handleRegenerateFeed} loading={isLoading}>
              {t('feeds_regenerate')}
            </StyledButton>
          )}
          {canManageFeeds && (
            <StyledButton htmlType="submit" loading={isLoading}>
              <CheckmarkIcon /> {t('feeds_save')}
            </StyledButton>
          )}
        </StyledButtonsWrap>
      </StyledForm>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        title={t('feeds_preview_xml')}
        footer={null}
        width={900}
      >
        <textarea
          readOnly
          value={previewXml || ''}
          style={{
            width: '100%',
            height: 500,
            fontSize: 12,
            fontFamily: 'monospace',
            background: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #d9d9d9',
            resize: 'vertical',
          }}
        />
      </Modal>
    </StyledWrapper>
  );
};

export default FeedForm;
